import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './PurchasingAdminPanel.css';
import { useAuth } from '../../../contexts/AuthContext';
import { API_CONFIG } from '../../../utils/apiConfig';

const AddPriceList = () => {
  const [validFrom, setValidFrom] = useState('');
  const [products, setProducts] = useState([
    { itemName: '', itemId: '', mrp: '', dp: '', sp: '', qty: '', profitPerPc: '', totalProfit: '' }
  ]);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  const API_BASE_URL = API_CONFIG.BASE_URL;

  // Get Firebase ID token for API calls
  const getAuthToken = useCallback(async () => {
    if (!currentUser) {
      throw new Error('No authenticated user');
    }
    return await currentUser.getIdToken(true);
  }, [currentUser]);

  const handleChange = (index, field, value) => {
    const updated = [...products];
    updated[index][field] = value;

    if (field === 'profitPerPc' || field === 'qty') {
      const profit = parseFloat(updated[index].profitPerPc || 0);
      const qty = parseInt(updated[index].qty || 0);
      updated[index].totalProfit = (profit * qty).toFixed(2);
    }

    setProducts(updated);
  };

  const handleAddRow = () => {
    setProducts([...products, { itemName: '', itemId: '', mrp: '', dp: '', sp: '', qty: '', profitPerPc: '', totalProfit: '' }]);
  };

  const handleDeleteRow = (index) => {
    if (products.length > 1) {
      const updatedProducts = products.filter((_, i) => i !== index);
      setProducts(updatedProducts);
    } else {
      alert('At least one product row is required');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check permissions - only Super Admin and Purchase Admin can access
    if (!userProfile) {
      setError('User profile not found. Please login again.');
      return;
    }

    const userRole = userProfile.role?.toLowerCase();
    const userDept = userProfile.department?.toLowerCase();
    
    if (userRole !== 'superadmin' && userRole !== 'super_admin' && 
        !(userRole === 'admin' && userDept === 'purchase')) {
      setError('Access denied. Only Super Admin and Purchase Admin can create Price Lists.');
      return;
    }
    
    try {
      const token = await getAuthToken();
      const headers = { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };


      const response = await fetch(`${API_BASE_URL}/api/price-lists`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ validFrom, products }),
      });

      const data = await response.json();

      if (!response.ok) throw new Error(data.message || 'Failed to save');

      alert('Price List Saved Successfully!');
      // Navigate based on user role
      if (userRole === 'superadmin' || userRole === 'super_admin') {
        navigate('/price-lists');
      } else {
        navigate('/purchasing-admin/price-lists');
      }
    } catch (err) {
      console.error('Submit Error:', err);
      alert(err.message || 'Something went wrong');
    }
  };

  const handlePrint = () => {
    // Create print content
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>WYENFOS PRICE LIST</title>
          <style>
            @page {
              margin: 20mm;
              size: A4;
            }
            body {
              font-family: Arial, sans-serif;
              margin: 0;
              padding: 20px;
              color: #333;
            }
            .print-header {
              text-align: center;
              margin-bottom: 30px;
              border-bottom: 2px solid #997a8d;
              padding-bottom: 20px;
            }
            .print-title {
              font-size: 24px;
              font-weight: bold;
              color: #997a8d;
              margin-bottom: 10px;
            }
            .print-date {
              font-size: 14px;
              color: #666;
              margin-bottom: 5px;
            }
            .print-valid-from {
              font-size: 12px;
              color: #888;
            }
            .print-table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
              font-size: 12px;
            }
            .print-table th {
              background-color: #997a8d;
              color: white;
              padding: 8px;
              text-align: center;
              font-weight: bold;
              border: 1px solid #333;
            }
            .print-table td {
              padding: 6px;
              border: 1px solid #333;
              text-align: center;
            }
            .print-table tr:nth-child(even) {
              background-color: #f9f9f9;
            }
            .print-footer {
              margin-top: 30px;
              text-align: center;
              font-size: 10px;
              color: #666;
              border-top: 1px solid #ddd;
              padding-top: 10px;
            }
            @media print {
              body { margin: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="print-header">
            <div class="print-title">WYENFOS PRICE LIST</div>
            <div class="print-date">Date: ${new Date().toLocaleDateString()}</div>
            ${validFrom ? `<div class="print-valid-from">Valid From: ${new Date(validFrom).toLocaleDateString()}</div>` : ''}
          </div>
          
          <table class="print-table">
            <thead>
              <tr>
                <th>NO</th>
                <th>ITEM NAME</th>
                <th>ITEM ID</th>
                <th>MRP</th>
                <th>DP /PCS</th>
                <th>SP</th>
                <th>QTY</th>
                <th>PROFIT /PCS</th>
                <th>TOTAL PROFIT</th>
              </tr>
            </thead>
            <tbody>
              ${products.map((p, i) => `
                <tr>
                  <td>${i + 1}</td>
                  <td>${p.itemName || ''}</td>
                  <td>${p.itemId || ''}</td>
                  <td>${p.mrp || ''}</td>
                  <td>${p.dp || ''}</td>
                  <td>${p.sp || ''}</td>
                  <td>${p.qty || ''}</td>
                  <td>${p.profitPerPc || ''}</td>
                  <td>${p.totalProfit || ''}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          
          <div class="print-footer">
            <p>Generated on ${new Date().toLocaleString()}</p>
            <p>WYENFOS PRICE LIST - CONFIDENTIAL</p>
          </div>
        </body>
      </html>
    `;

    // Open print window
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    
    // Wait for content to load, then print
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 250);
    };
  };

  return (
    <div className="price-list-container">
      <style>
        {`
          /* Responsive CSS for Price List */
          .price-list-container {
            padding: 20px;
            max-width: 100%;
            overflow-x: auto;
          }
          
          .responsive-table-container {
            overflow-x: auto;
            margin: 20px 0;
          }
          
          .price-list-table {
            width: 100%;
            min-width: 800px;
            border-collapse: collapse;
            margin: 20px 0;
            font-size: 14px;
          }
          
          .price-list-table th,
          .price-list-table td {
            border: 1px solid #ddd;
            padding: 8px;
            text-align: left;
          }
          
          .price-list-table th {
            background-color: #b39eb5;
            font-weight: bold;
            font-size: 12px;
            white-space: nowrap;
          }
          
          .price-list-table input {
            width: 100%;
            border: 1px solid #ccc;
            padding: 4px;
            font-size: 12px;
            box-sizing: border-box;
          }
          
          .mobile-cards {
            display: none;
          }
          
          .mobile-card {
            background: #fff;
            border: 1px solid #ddd;
            border-radius: 8px;
            padding: 15px;
            margin-bottom: 15px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          }
          
          .mobile-card-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 10px;
            padding-bottom: 10px;
            border-bottom: 1px solid #eee;
          }
          
          .mobile-card-title {
            font-weight: bold;
            color: #333;
          }
          
          .mobile-card-row {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 10px;
            margin-bottom: 10px;
            font-size: 14px;
          }
          
          .mobile-card-label {
            font-weight: bold;
            color: #666;
          }
          
          .mobile-card-input {
            width: 100%;
            border: 1px solid #ccc;
            padding: 6px;
            border-radius: 4px;
            font-size: 14px;
          }
          
          .button-group {
            display: flex;
            gap: 10px;
            flex-wrap: wrap;
            margin-top: 20px;
          }
          
          .button-group button {
            padding: 10px 15px;
            border: none;
            border-radius: 4px;
            cursor: pointer;
            font-size: 14px;
            font-weight: bold;
            white-space: nowrap;
            width: auto;
            min-width: auto;
          }
          
          .back-button {
            background-color: #6c757d;
            color: white;
          }
          
          .add-button {
            background-color: #28a745;
            color: white;
          }
          
          .save-button {
            background-color: #007bff;
            color: white;
          }
          
          .print-button {
            background-color: #6f42c1;
            color: white;
          }
          
          .trash-button {
            background-color: #dc3545;
            color: white;
            padding: 4px 8px;
            border: none;
            border-radius: 3px;
            cursor: pointer;
            font-size: 12px;
            width: auto;
            min-width: auto;
          }
          
          .trash-button:hover {
            background-color: #c82333;
          }
          
          .date-input-container {
            margin: 20px 0;
            display: flex;
            align-items: center;
            gap: 10px;
            flex-wrap: wrap;
          }
          
          .date-input-container label {
            font-weight: bold;
            color: #333;
          }
          
          .date-input-container input {
            padding: 8px;
            border: 1px solid #ccc;
            border-radius: 4px;
            font-size: 14px;
          }
          
          /* Mobile Styles */
          @media (max-width: 768px) {
            .price-list-container {
              padding: 10px;
            }
            
            .desktop-table {
              display: none !important;
            }
            
            .mobile-cards {
              display: block !important;
            }
            
            .button-group {
              flex-direction: column;
            }
            
            .button-group button {
              width: auto;
              min-width: auto;
              margin-bottom: 10px;
              flex: 1;
            }
            
            .date-input-container {
              flex-direction: column;
              align-items: flex-start;
            }
          }
          
          /* Tablet Styles */
          @media (max-width: 1024px) and (min-width: 769px) {
            .price-list-table {
              font-size: 12px;
            }
            
            .price-list-table th,
            .price-list-table td {
              padding: 6px;
            }
            
            .price-list-table input {
              font-size: 11px;
              padding: 3px;
            }
          }
          
          @media (min-width: 769px) {
            .desktop-table {
              display: block !important;
            }
            .mobile-cards {
              display: none !important;
            }
          }
        `}
      </style>
      
      <button 
        className="bck-button" 
        onClick={() => {
          // Navigate back based on user role
          if (userProfile?.role?.toLowerCase() === 'superadmin' || userProfile?.role?.toLowerCase() === 'super_admin') {
            navigate('/price-lists');
          } else {
            navigate('/purchasing-admin/price-lists');
          }
        }}
        style={{
          backgroundColor: '#997a8d',
          color: 'white',
          border: 'none',
          padding: '10px 20px',
          borderRadius: '5px',
          cursor: 'pointer',
          fontSize: '14px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}
      >
        ← Back to Price Lists
      </button>
      
      <h2>WYENFOS PRICE LIST</h2>
      {error && <p className="error" style={{ color: 'red', marginBottom: '10px' }}>{error}</p>}
      
      <div className="date-input-container">
        <label>Valid from:</label>
        <input type="date" value={validFrom} onChange={e => setValidFrom(e.target.value)} />
      </div>
      
      <form onSubmit={handleSubmit}>
        {/* Desktop Table View */}
        <div className="desktop-table">
          <div className="responsive-table-container">
            <table className="price-list-table">
              <thead>
                <tr>
                  <th>NO</th>
                  <th>ITEM NAME</th>
                  <th>ITEM ID</th>
                  <th>MRP</th>
                  <th>DP /PCS</th>
                  <th>SP</th>
                  <th>QTY</th>
                  <th>PROFIT /PCS</th>
                  <th>TOTAL PROFIT</th>
                  <th>ACTION</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p, i) => (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    <td><input value={p.itemName} onChange={e => handleChange(i, 'itemName', e.target.value)} /></td>
                    <td><input value={p.itemId} onChange={e => handleChange(i, 'itemId', e.target.value)} /></td>
                    <td><input value={p.mrp} onChange={e => handleChange(i, 'mrp', e.target.value)} /></td>
                    <td><input value={p.dp} onChange={e => handleChange(i, 'dp', e.target.value)} /></td>
                    <td><input value={p.sp} onChange={e => handleChange(i, 'sp', e.target.value)} /></td>
                    <td><input value={p.qty} onChange={e => handleChange(i, 'qty', e.target.value)} /></td>
                    <td><input value={p.profitPerPc} onChange={e => handleChange(i, 'profitPerPc', e.target.value)} /></td>
                    <td>{p.totalProfit}</td>
                    <td>
                      <button 
                        type="button" 
                        className="trash-button" 
                        onClick={() => handleDeleteRow(i)}
                        title="Delete this row"
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Mobile Card View */}
        <div className="mobile-cards">
          {products.map((p, i) => (
            <div key={i} className="mobile-card">
              <div className="mobile-card-header">
                <div className="mobile-card-title">Item #{i + 1}</div>
                <button 
                  type="button" 
                  className="trash-button" 
                  onClick={() => handleDeleteRow(i)}
                  title="Delete this item"
                >
                  🗑️
                </button>
              </div>
              
              <div className="mobile-card-row">
                <div className="mobile-card-label">Item Name:</div>
                <input 
                  className="mobile-card-input"
                  value={p.itemName} 
                  onChange={e => handleChange(i, 'itemName', e.target.value)}
                  placeholder="Item Name"
                />
              </div>
              
              <div className="mobile-card-row">
                <div className="mobile-card-label">Item ID:</div>
                <input 
                  className="mobile-card-input"
                  value={p.itemId} 
                  onChange={e => handleChange(i, 'itemId', e.target.value)}
                  placeholder="Item ID"
                />
              </div>
              
              <div className="mobile-card-row">
                <div className="mobile-card-label">MRP:</div>
                <input 
                  className="mobile-card-input"
                  value={p.mrp} 
                  onChange={e => handleChange(i, 'mrp', e.target.value)}
                  placeholder="MRP"
                />
              </div>
              
              <div className="mobile-card-row">
                <div className="mobile-card-label">DP/PCS:</div>
                <input 
                  className="mobile-card-input"
                  value={p.dp} 
                  onChange={e => handleChange(i, 'dp', e.target.value)}
                  placeholder="DP/PCS"
                />
              </div>
              
              <div className="mobile-card-row">
                <div className="mobile-card-label">SP:</div>
                <input 
                  className="mobile-card-input"
                  value={p.sp} 
                  onChange={e => handleChange(i, 'sp', e.target.value)}
                  placeholder="SP"
                />
              </div>
              
              <div className="mobile-card-row">
                <div className="mobile-card-label">Quantity:</div>
                <input 
                  className="mobile-card-input"
                  value={p.qty} 
                  onChange={e => handleChange(i, 'qty', e.target.value)}
                  placeholder="Quantity"
                />
              </div>
              
              <div className="mobile-card-row">
                <div className="mobile-card-label">Profit/PCS:</div>
                <input 
                  className="mobile-card-input"
                  value={p.profitPerPc} 
                  onChange={e => handleChange(i, 'profitPerPc', e.target.value)}
                  placeholder="Profit/PCS"
                />
              </div>
              
              <div className="mobile-card-row">
                <div className="mobile-card-label">Total Profit:</div>
                <div style={{ padding: '6px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                  {p.totalProfit}
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <div className="button-group">
          <button type="button" className="back-button" onClick={() => {
            // Navigate back based on user role
            if (userProfile?.role?.toLowerCase() === 'superadmin' || userProfile?.role?.toLowerCase() === 'super_admin') {
              navigate('/price-lists');
            } else {
              navigate('/purchasing-admin/price-lists');
            }
          }}>Back</button>
          <button type="button" className="add-button" onClick={handleAddRow}>Add Row</button>
          <button type="button" className="print-button" onClick={handlePrint}>🖨️ Print</button>
          <button type="submit" className="save-button">Save Price List</button>
        </div>
      </form>
    </div>
  );
};

export default AddPriceList;